/**
 * WebSocket hook for chat threads.
 *
 * Connects to `/api/chat/ws/{threadId}` using the access_token cookie (already
 * set by login). Emits `onMessage` callbacks for incoming messages, presence
 * updates, and read receipts. Auto-reconnects with exponential backoff.
 */
import { useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function wsUrl(threadId) {
    // Convert https://lawfirm-hub-staging.preview.emergentagent.com → wss://x.preview.emergentagent.com
    if (!BACKEND) return "";
    const u = new URL(BACKEND);
    const scheme = u.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${u.host}/api/chat/ws/${threadId}`;
}

export function useChatSocket(threadId, { onMessage } = {}) {
    const [status, setStatus] = useState("idle"); // idle | connecting | open | closed
    const wsRef = useRef(null);
    const retryRef = useRef(0);
    const pingTimerRef = useRef(null);
    const stoppedRef = useRef(false);
    const sendOnSocket = useRef(null);

    useEffect(() => {
        if (!threadId) return undefined;
        stoppedRef.current = false;

        const connect = () => {
            setStatus("connecting");
            try {
                const ws = new WebSocket(wsUrl(threadId));
                wsRef.current = ws;

                ws.onopen = () => {
                    setStatus("open");
                    retryRef.current = 0;
                    sendOnSocket.current = (payload) => {
                        try { ws.send(JSON.stringify(payload)); } catch {}
                    };
                    // Heartbeat
                    pingTimerRef.current = setInterval(() => {
                        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
                    }, 25000);
                };
                ws.onmessage = (ev) => {
                    try {
                        const data = JSON.parse(ev.data);
                        if (onMessage) onMessage(data);
                    } catch {}
                };
                ws.onerror = () => { /* close handler will fire next */ };
                ws.onclose = () => {
                    setStatus("closed");
                    if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
                    if (stoppedRef.current) return;
                    // Exponential backoff: 1s, 2s, 4s, … cap 30s
                    const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
                    setTimeout(() => { if (!stoppedRef.current) connect(); }, delay);
                };
            } catch (e) {
                setStatus("closed");
            }
        };
        connect();

        return () => {
            stoppedRef.current = true;
            if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
            try { wsRef.current?.close(); } catch {}
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadId]);

    return { status, send: (payload) => sendOnSocket.current && sendOnSocket.current(payload) };
}
